/**
 * Map global (super) question API records ↔ visual editor state.
 * Super stores `definition` as JSON text or object with `ui`, `actions`, etc.
 */

export function parseQuestionDefinition(definition) {
    if (definition == null) return {};
    if (typeof definition === 'string') {
        try {
            return JSON.parse(definition);
        } catch {
            return {};
        }
    }
    return typeof definition === 'object' ? { ...definition } : {};
}

/**
 * Shape expected by {@link VisualEditor} `initialQuestion` + `normalizeLoadedQuestion`.
 */
export function mapSuperApiQuestionToEditor(apiQuestion) {
    const def = parseQuestionDefinition(apiQuestion?.definition);
    const ui = def.ui || {
        blocks: [],
        presentation: null,
        minimumBlockCount: 1,
    };
    return {
        id: apiQuestion.id,
        unique_id: apiQuestion.unique_id,
        owner_unique_id: apiQuestion.owner_unique_id ?? null,
        name: apiQuestion.name || 'New Question',
        description: apiQuestion.adminDescription || '',
        area: apiQuestion.area || def.area || 'General',
        difficulty: apiQuestion.difficulty || def.difficulty || 'basic',
        behaviours: apiQuestion.behaviours || def.behaviours || [],
        languages: apiQuestion.languages || def.languages || ['en'],
        tags: apiQuestion.tags || [],
        actions: def.actions ?? {},
        translations: apiQuestion.translations ?? def.translations,
        type: def.type ?? apiQuestion.type,
        components: apiQuestion.components ?? def.components ?? 'Behaviour',
        max_score: apiQuestion.max_score,
        external_id: apiQuestion.external_id,
        ui: {
            blocks: ui.blocks || [],
            presentation: ui.presentation ?? null,
            minimumBlockCount: ui.minimumBlockCount ?? 1,
        },
    };
}

/**
 * Merge editor question back onto the super API payload (keeps acl, order_number, …).
 */
export function mergeEditorIntoSuperQuestion(editorQuestion, originalApiQuestion) {
    const prevDef = parseQuestionDefinition(originalApiQuestion?.definition);
    const nextDef = {
        ...prevDef,
        ui: {
            ...(prevDef.ui || {}),
            ...(editorQuestion.ui || {}),
            blocks: editorQuestion.ui?.blocks ?? [],
            presentation: editorQuestion.ui?.presentation ?? null,
            minimumBlockCount:
                editorQuestion.ui?.minimumBlockCount ??
                prevDef.ui?.minimumBlockCount ??
                1,
        },
        actions: editorQuestion.actions ?? prevDef.actions ?? {},
        languages: editorQuestion.languages || prevDef.languages,
        type: editorQuestion.type ?? prevDef.type,
        difficulty: editorQuestion.difficulty ?? prevDef.difficulty,
    };

    const originalDef = originalApiQuestion?.definition;
    const useString =
        typeof originalDef === 'string' || originalDef == null || originalDef === '';

    return {
        ...originalApiQuestion,
        name: editorQuestion.name,
        adminDescription:
            editorQuestion.description ?? editorQuestion.adminDescription,
        area: editorQuestion.area,
        difficulty: editorQuestion.difficulty,
        components: editorQuestion.components,
        max_score: editorQuestion.max_score ?? originalApiQuestion?.max_score,
        languages: editorQuestion.languages,
        tags: editorQuestion.tags ?? originalApiQuestion?.tags,
        thumbnail:
            editorQuestion.thumbnail ?? originalApiQuestion?.thumbnail,
        behaviours:
            editorQuestion.behaviours ?? originalApiQuestion?.behaviours,
        translations:
            editorQuestion.translations ?? originalApiQuestion?.translations,
        definition: useString
            ? JSON.stringify(nextDef, null, 2)
            : nextDef,
    };
}
